import { mapActions } from 'vuex';
const sleep = (time) => new Promise((resolve) => setTimeout(resolve, time));

export default {
  computed: {
    // Master does not map `status` here: consumers that need the calendar-unauthorized check map
    // the store getter themselves (InsightsMeetingsWidget); everywhere else `this.status` is
    // undefined and isIntegrationUnauthorized stays false. Vue 3 warns on reading an undeclared
    // instance property, so declare the master-equivalent default; a consumer's own mapGetters
    // overrides this.
    status: () => undefined,
    integrationExists() {
      if (this.isIntegrationUnauthorized) {
        return false;
      }

      return this.isIntegrationSynced || this.isIntegrationSyncing;
    },
    isIntegrationUnavailable() {
      return this.integrationStatus === 'unavailable';
    },
    isIntegrationSynced() {
      return this.integrationStatus === 'synced';
    },
    isIntegrationUnsynced() {
      return this.integrationStatus === 'unsynced';
    },
    isIntegrationSyncing() {
      return this.integrationStatus === 'syncing';
    },
    isIntegrationUnauthorized() {
      return (
        this.status &&
        this.status?.code === 403 &&
        this.status?.event === 'calendar_integration' &&
        this.status?.message === 'unauthorized'
      );
    },
    nextRetry() {
      return this.retryFetch?.length ? this.retryFetch[0] : {};
    },
    isStillSyncing() {
      return this.isIntegrationSyncing && this.nextRetry.repeat && this.nextRetry.repeat > 0;
    },
    hasRequiredFilters() {
      return this.filters?.date?.from && this.filters?.time_zone;
    },
  },
  data: () => ({
    showFailureDialog: false,
  }),
  watch: {
    hasRequiredFilters(newVal) {
      // Fetch when required filters become available and widget has no data yet
      if (newVal && (!this.widgetData || Object.keys(this.widgetData).length === 0)) {
        this.fetch();
      }
    },
  },
  methods: {
    ...mapActions(['fetchWidgets']),
    async fetch() {
      // Skip fetching if widget data already exists (fetched globally)
      if (this.widgetData && Object.keys(this.widgetData).length > 0) {
        return;
      }

      // Skip fetching if required filters are not set (prevents fetching with incomplete params)
      if (!this.hasRequiredFilters) {
        return;
      }

      // Widgets with integrations need to be fetched a bit differently:
      // If the status is 'syncing', we will attempt to fetch 2 more times, and only consider the request as a
      // "failure" afterwards. This will render a different component where we ask the user to refresh manually.

      try {
        await this.fetchWidgets({ widgetKey: this.widgetKey });
      } catch (e) {
        console.error(e);
      } finally {
        const attempt = this.retryFetch?.length ? this.retryFetch[0] : null;

        if (this.isIntegrationSyncing && attempt && attempt.repeat > 0) {
          await sleep(attempt.every * 1000);

          // Decrement the number of repetitions still in queue for this attempt
          attempt.repeat--;

          // No more repetitions for this attempt? Remove the attempt from this.retryFetch
          if (!attempt.repeat) {
            this.retryFetch.shift();
          }

          this.fetch();
        }
      }
    },
    toggleFailureDialog() {
      this.showFailureDialog = !this.showFailureDialog;
    },
    checkFailureStatus() {
      if (this.isIntegrationUnauthorized) {
        this.showFailureDialog = true;
      }
    },
  },
};
